// Workforce › Intake (`/c/{slug}/agents/intake`; INC2-INTAKE, build order increment 2 A): the
// operator's view of the one intake — what came in (a vendor notice, a webhook, a form), what
// became of it (a task for its handler, logged, dropped) — and the integration work that died
// (Retry, Discard as hover icons). Managers only; never a payload.
// Calls on load: `intake.events` (Received) or `intake.jobs?state=dead` (Dead letters).
import type { OperationOutput } from "@companyos/sdk";
import { Badge, Button, EmptyState, InfoTip, PageHeader, ViewTabs } from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { InboxIcon, RotateCcwIcon, Trash2Icon } from "lucide-react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { problemText } from "../de/de-status";
import { formatDateTime } from "../format";
import { LoadError, Loading } from "../screen-parts";
import { RowAction, RowActions } from "../settings/row-actions";

export type InboundEventRow = OperationOutput<"intake.events">["data"][number];
export type IntegrationJobRow = OperationOutput<"intake.jobs">["data"][number];
export const INTAKE_TABS = ["events", "dead"] as const;
export type IntakeTab = (typeof INTAKE_TABS)[number];

const TAB_LABELS: Record<IntakeTab, string> = { events: "Received", dead: "Dead letters" };

const STATUS: Record<
  InboundEventRow["status"],
  [string, "neutral" | "info" | "success" | "warning" | "danger"]
> = {
  received: ["Received", "info"],
  tasked: ["Task made", "success"],
  logged: ["Logged", "neutral"],
  dropped: ["Dropped", "warning"],
};

const SOURCE: Record<InboundEventRow["source_kind"], string> = {
  hook: "Webhook",
  link: "Link",
  mailbox: "Mailbox",
  form: "Form",
  ghl_location: "GoHighLevel",
  chat_message: "Chat",
  hubspot_portal: "HubSpot",
  google: "Google",
  // ONBOARDING-P4: mail to the client's support address (the HTVA team answers it)
  system_address: "Support address",
};

const JOB_KIND: Record<IntegrationJobRow["kind"], string> = {
  pull: "Pull",
  push: "Push",
  act: "Act",
  renew: "Renew",
};

export const intakeKey = (co: string) => ["company", co, "intake"] as const;

export function IntakeScreen({
  api,
  company,
  tab,
  onTab,
}: {
  api: Api;
  company: CompanyAccess;
  tab: IntakeTab;
  onTab: (t: IntakeTab) => void;
}) {
  const co = company.id;
  return (
    <>
      <PageHeader
        spacing="tabs"
        eyebrow="Team"
        title="Intake"
        badge={
          <InfoTip label="About Intake">
            Everything that came in — vendor notices, webhooks, forms — and what became of it: a
            task for its handler, logged when no rule names one, or dropped. A notice is a reason to
            fetch, never the data. Dead letters are integration work that failed for good.
          </InfoTip>
        }
      />
      <ViewTabs
        aria-label="Intake"
        value={tab}
        onValueChange={(v) => onTab(v as IntakeTab)}
        tabs={INTAKE_TABS.map((t) => ({ value: t, label: TAB_LABELS[t] }))}
      />
      <div className="flex min-w-0 flex-col gap-3 pt-5">
        {tab === "events" ? <Received api={api} co={co} /> : <DeadLetters api={api} co={co} />}
      </div>
    </>
  );
}

function Received({ api, co }: { api: Api; co: string }) {
  const q = useInfiniteQuery({
    queryKey: [...intakeKey(co), "events"],
    queryFn: ({ pageParam }) =>
      api.client.intake.events({
        params: { co },
        query: { limit: 50, ...(pageParam ? { cursor: pageParam } : {}) },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
  });
  const rows = (q.data?.pages ?? []).flatMap((p) => p.data);
  if (q.isPending) return <Loading label="Loading intake" rows={4} />;
  if (q.isError) return <LoadError what="Intake" error={q.error} />;
  if (!rows.length)
    return (
      <EmptyState
        variant="card"
        icon={InboxIcon}
        title="Nothing received yet"
        description="Notices, webhooks and form entries appear here."
      />
    );
  return (
    <>
      <ul aria-label="Received" className="flex flex-col" data-slot="intake-events">
        {rows.map((x) => {
          const [label, tone] = STATUS[x.status];
          return (
            <li
              key={x.id}
              data-status={x.status}
              className="flex min-h-10 items-center gap-3 border-b border-border px-1 py-2 text-[13px]"
            >
              <Badge variant={tone}>{label}</Badge>
              <span className="w-24 shrink-0 text-muted-foreground">{SOURCE[x.source_kind]}</span>
              <span className="min-w-0 flex-1 truncate" title={x.label}>
                {x.label}
              </span>
              {x.notice ? <Badge variant="neutral">Notice</Badge> : null}
              {x.screen.quarantined ? <Badge variant="danger">Withheld</Badge> : null}
              {x.drop_reason ? <InfoTip label={`Why: ${x.label}`}>{x.drop_reason}</InfoTip> : null}
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {formatDateTime(x.received_at)}
              </span>
            </li>
          );
        })}
      </ul>
      <More q={q} />
    </>
  );
}

function DeadLetters({ api, co }: { api: Api; co: string }) {
  const qc = useQueryClient();
  const q = useInfiniteQuery({
    queryKey: [...intakeKey(co), "dead"],
    queryFn: ({ pageParam }) =>
      api.client.intake.jobs({
        params: { co },
        query: { state: "dead", limit: 50, ...(pageParam ? { cursor: pageParam } : {}) },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
  });
  const retry = useMutation({
    mutationFn: (id: string) => api.client.intake.retryJob({ params: { co, id } }),
    onSettled: () => qc.invalidateQueries({ queryKey: intakeKey(co) }),
  });
  const discard = useMutation({
    mutationFn: (id: string) => api.client.intake.discardJob({ params: { co, id } }),
    onSettled: () => qc.invalidateQueries({ queryKey: intakeKey(co) }),
  });
  const rows = (q.data?.pages ?? []).flatMap((p) => p.data);
  const failure = retry.error ?? discard.error;
  return (
    <>
      {failure ? (
        <p role="alert" className="text-sm text-destructive">
          {problemText(failure, "That did not work. Try again.")}
        </p>
      ) : null}
      {q.isPending ? (
        <Loading label="Loading dead letters" rows={3} />
      ) : q.isError ? (
        <LoadError what="Dead letters" error={q.error} />
      ) : !rows.length ? (
        <EmptyState
          variant="card"
          icon={InboxIcon}
          title="No dead letters"
          description="Integration work that fails for good appears here."
        />
      ) : (
        <ul aria-label="Dead letters" className="flex flex-col" data-slot="intake-dead">
          {rows.map((x) => {
            const name = `${JOB_KIND[x.kind]} · ${x.provider}`;
            return (
              <li
                key={x.id}
                data-state={x.state}
                className="group/row flex min-h-10 items-center gap-3 border-b border-border px-1 py-2 text-[13px]"
              >
                <Badge variant="danger">Dead letter</Badge>
                <span className="min-w-0 flex-1 truncate" title={name}>
                  {name}
                </span>
                {x.dead_reason ? <InfoTip label={`Why: ${name}`}>{x.dead_reason}</InfoTip> : null}
                <span className="shrink-0 tabular-nums text-muted-foreground">
                  {formatDateTime(x.updated_at)}
                </span>
                <RowActions className="w-16">
                  <RowAction
                    icon={RotateCcwIcon}
                    label={`Retry ${name}`}
                    tip="Retry"
                    disabled={retry.isPending}
                    onClick={() => retry.mutate(x.id)}
                  />
                  <RowAction
                    icon={Trash2Icon}
                    label={`Discard ${name}`}
                    tip="Discard"
                    disabled={discard.isPending}
                    onClick={() => discard.mutate(x.id)}
                  />
                </RowActions>
              </li>
            );
          })}
        </ul>
      )}
      <More q={q} />
    </>
  );
}

function More({
  q,
}: {
  q: { hasNextPage: boolean; isFetchingNextPage: boolean; fetchNextPage: () => unknown };
}) {
  if (!q.hasNextPage) return null;
  return (
    <Button
      variant="ghost"
      size="sm"
      className="self-start"
      disabled={q.isFetchingNextPage}
      onClick={() => void q.fetchNextPage()}
    >
      Load more
    </Button>
  );
}
